// ProductFields.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPlus, 
  FiEdit2, 
  FiSearch,
  FiX,
  FiCheck,
  FiPackage,
  FiEye,
  FiGrid,
  FiList,
  FiChevronDown,
  FiChevronUp,
  FiInfo,
  FiFilter,
  FiTrendingUp,
  FiClock,
  FiCalendar,
  FiTag,
  FiBox,
  FiCheckCircle,
  FiMinusCircle,
  FiSave,
  FiRefreshCw,
  FiAlertCircle,
  FiShoppingBag,
  FiLayers,
  FiHash,
  FiType,
  FiAlignLeft,
  FiTrash2,
  FiArrowUp,
  FiArrowDown
} from 'react-icons/fi';
import { productFieldAPI, productAPI, accountAPI } from '../utils/api';
import toast from 'react-hot-toast';
import useResponsive from '../hooks/useResponsive';

const ProductFields = ({ initialProductId }) => {
  const [fields, setFields] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);
  const [modalType, setModalType] = useState('create');
  const [selectedField, setSelectedField] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [requiredFilter, setRequiredFilter] = useState('all');
  const [sortBy, setSortBy] = useState('sort_order');
  const [userRole, setUserRole] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(initialProductId || '');
  const [viewMode, setViewMode] = useState('table');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const [formData, setFormData] = useState({
    name: '',
    label: '',
    type: 'any',
    sort_order: 1,
    required: true
  });

  const { isMobile, isTablet, isDesktop } = useResponsive();

  // Color theme based on product fields (indigo)
  const theme = {
    primary: 'from-indigo-600 to-blue-600',
    primaryLight: 'from-indigo-500 to-blue-500',
    secondary: 'from-indigo-500 to-purple-500',
    bg: 'bg-indigo-100 dark:bg-indigo-900/30',
    text: 'text-indigo-600 dark:text-indigo-400',
    border: 'border-indigo-200 dark:border-indigo-800',
    ring: 'ring-indigo-500',
    hover: 'hover:bg-indigo-50 dark:hover:bg-indigo-900/20',
    gradient: 'from-indigo-600 to-blue-600',
    lightBg: 'bg-indigo-50 dark:bg-indigo-900/20',
    badge: {
      success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      required: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      optional: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
    }
  };

  useEffect(() => {
    fetchUserAndData();
  }, []);

  useEffect(() => {
    if (selectedProductId) {
      fetchFields(selectedProductId);
    } else {
      setFields([]);
    }
  }, [selectedProductId]);

  const fetchUserAndData = async () => {
    try {
      setLoading(true);
      const userResponse = await accountAPI.getMe();
      if (userResponse.data.success) {
        const user = userResponse.data.data;
        setCurrentUser(user);
        setUserRole(user.role);
      }

      const productsResponse = await productAPI.list();
      if (productsResponse.data && productsResponse.data.success) {
        let allProducts = productsResponse.data.data || [];
        setProducts(allProducts);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFields = async (productId) => {
    if (!productId) return;
    
    setFieldsLoading(true);
    try {
      const response = await productFieldAPI.list(productId);
      if (response.data && response.data.success) {
        setFields(response.data.data || []);
      } else {
        setFields([]);
      }
    } catch (error) {
      console.error('Error fetching fields:', error);
      setFields([]);
    } finally {
      setFieldsLoading(false);
    }
  };

    const canView = () => userRole === 'system'; // Only system can view
    const canCreate = () => userRole === 'system';
    const canEdit = () => userRole === 'system';
    const canDelete = () => userRole === 'system';

  const getProductDetails = (productId) => {
    return products.find(p => p.id === parseInt(productId));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name) {
      errors.name = 'Field name is required';
    } else {
      const nameRegex = /^[a-z_]+$/;
      if (!nameRegex.test(formData.name)) {
        errors.name = 'Only lowercase letters and underscores allowed';
      }
    }

    if (!formData.label) {
      errors.label = 'Field label is required';
    }

    if (!formData.type) {
      errors.type = 'Field type is required';
    }

    if (!formData.sort_order || formData.sort_order < 1) {
      errors.sort_order = 'Sort order must be at least 1';
    }

    if (!selectedProductId) {
      errors.product = 'Please select a product';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!canCreate()) {
      toast.error('You do not have permission to create fields');
      return;
    }

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await productFieldAPI.create({
        product_id: parseInt(selectedProductId),
        name: formData.name,
        label: formData.label,
        type: formData.type,
        sort_order: parseInt(formData.sort_order),
        required: formData.required ? 1 : 0
      });
      
      if (response.data && response.data.success) {
        toast.success('Field created successfully');
        setShowModal(false);
        resetForm();
        fetchFields(selectedProductId);
      } else {
        toast.error(response.data?.message || 'Failed to create field');
      }
    } catch (error) {
      console.error('Error creating field:', error);
      toast.error(error.response?.data?.message || 'Failed to create field');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!canEdit()) {
      toast.error('You do not have permission to update fields');
      return;
    }

    if (!selectedField) {
      toast.error('No field selected');
      return;
    }

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    const updateData = { field_id: selectedField.id };

    if (formData.name !== selectedField.name) {
      updateData.name = formData.name;
    }
    
    if (formData.label !== selectedField.label) {
      updateData.label = formData.label;
    }
    
    if (formData.type !== selectedField.type) {
      updateData.type = formData.type;
    }

    if (parseInt(formData.sort_order) !== selectedField.sort_order) {
      updateData.sort_order = parseInt(formData.sort_order);
    }

    if (formData.required !== (selectedField.required === 1)) {
      updateData.required = formData.required ? 1 : 0;
    }

    if (Object.keys(updateData).length === 1) {
      toast.error('No changes to update');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await productFieldAPI.update(updateData);
      
      if (response.data && response.data.success) {
        toast.success('Field updated successfully');
        setShowModal(false);
        resetForm();
        fetchFields(selectedProductId);
      } else {
        toast.error(response.data?.message || 'Failed to update field');
      }
    } catch (error) {
      console.error('Error updating field:', error);
      toast.error(error.response?.data?.message || 'Failed to update field');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    const allowedValue = value.toLowerCase().replace(/[^a-z_]/g, '');
    setFormData({ ...formData, name: allowedValue });
    if (formErrors.name) {
      setFormErrors({ ...formErrors, name: null });
    }
  };

  const confirmDelete = (field) => {
    setFieldToDelete(field);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!canDelete() || !fieldToDelete) return;

    try {
      const response = await productFieldAPI.delete(fieldToDelete.id);
      if (response.data && response.data.success) {
        toast.success('Field deleted successfully');
        fetchFields(selectedProductId);
      } else {
        toast.error(response.data?.message || 'Failed to delete field');
      }
    } catch (error) {
      console.error('Error deleting field:', error);
      toast.error(error.response?.data?.message || 'Failed to delete field');
    } finally {
      setShowDeleteModal(false);
      setFieldToDelete(null);
    }
  };

  const moveField = async (field, direction) => {
    if (!canEdit()) {
      toast.error('You do not have permission to reorder fields');
      return;
    }

    const currentSort = field.sort_order;
    const newSort = direction === 'up' ? currentSort - 1 : currentSort + 1;

    // Find the field that currently has the target sort order
    const targetField = fields.find(f => f.sort_order === newSort);
    if (!targetField) return;

    try {
      // Update current field
      await productFieldAPI.update({
        field_id: field.id,
        sort_order: newSort
      });

      // Update target field
      await productFieldAPI.update({
        field_id: targetField.id,
        sort_order: currentSort
      });

      toast.success('Fields reordered successfully');
      fetchFields(selectedProductId);
    } catch (error) {
      console.error('Error reordering fields:', error);
      toast.error('Failed to reorder fields');
    }
  };

  const openEditModal = (field) => {
    if (!canEdit()) {
      toast.error('You do not have permission to edit fields');
      return;
    }

    setSelectedField(field);
    setFormData({
      name: field.name,
      label: field.label,
      type: field.type,
      sort_order: field.sort_order,
      required: field.required === 1
    });
    setModalType('edit');
    setShowModal(true);
    setFormErrors({});
  };

  const openCreateModal = () => {
    if (!canCreate()) {
      toast.error('You do not have permission to create fields');
      return;
    }

    if (!selectedProductId) {
      toast.error('Please select a product first');
      return;
    }

    // Calculate next sort order
    const nextSort = fields.length > 0 
      ? Math.max(...fields.map(f => f.sort_order)) + 1 
      : 1;

    resetForm();
    setFormData(prev => ({ ...prev, sort_order: nextSort }));
    setModalType('create');
    setShowModal(true);
    setFormErrors({});
  };

  const resetForm = () => {
    setFormData({
      name: '',
      label: '',
      type: 'any',
      sort_order: 1,
      required: true
    });
    setSelectedField(null);
    setFormErrors({});
  };

  const handleProductChange = (productId) => {
    setSelectedProductId(productId);
    setFields([]);
    if (productId) {
      fetchFields(productId);
    }
  };

  const filteredProducts = products.filter(product => {
    // Exclude all stock-based products (is_stock_based === 1)
    if (product.is_stock_based === 1) {
      return false;
    }
    
    // Then apply the search filter
    return product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
          product.code.toLowerCase().includes(productSearch.toLowerCase());
  });

  const selectedProduct = getProductDetails(selectedProductId);

  const getSortedFields = () => {
    let filtered = fields.filter(field => {
      const matchesSearch = field.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           field.label.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesRequired = requiredFilter === 'all' || 
                             (requiredFilter === 'required' && field.required === 1) ||
                             (requiredFilter === 'optional' && field.required === 0);
      
      return matchesSearch && matchesRequired;
    });

    switch(sortBy) {
      case 'sort_order':
        return filtered.sort((a, b) => a.sort_order - b.sort_order);
      case 'name':
        return filtered.sort((a, b) => a.name.localeCompare(b.name));
      case 'label':
        return filtered.sort((a, b) => a.label.localeCompare(b.label));
      case 'type':
        return filtered.sort((a, b) => a.type.localeCompare(b.type));
      default:
        return filtered;
    }
  };

  const displayedFields = getSortedFields();

  const totalFields = displayedFields.length;
  const requiredFields = displayedFields.filter(f => f.required === 1).length;
  const optionalFields = displayedFields.filter(f => f.required === 0).length;

  const getTypeLabel = (type) => {
    const types = {
      any: 'Any',
      digit: 'Digits Only',
      alphabet: 'Alphabet Only',
      alphanumeric: 'Alphanumeric',
      lower: 'Lowercase',
      upper: 'Uppercase'
    };
    return types[type] || type;
  };

  const FieldGridCard = ({ field }) => {
    const canEditThis = canEdit();
    
    return (
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        whileHover={{ y: -4 }}
        className="group bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between mb-4">
            <div className={`w-10 h-10 rounded-xl ${theme.bg} flex items-center justify-center`}>
              <FiHash className={`w-5 h-5 ${theme.text}`} />
            </div>
            <span className="text-xs text-gray-400">Order: {field.sort_order}</span>
          </div>

          <h3 className="font-semibold text-gray-900 dark:text-white text-lg mb-1">
            {field.label}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
            <FiTag className="w-3 h-3" />
            {field.name}
          </p>

          <div className="flex flex-wrap gap-2 mt-auto">
            <span className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
              field.required === 1 ? theme.badge.required : theme.badge.optional
            }`}>
              {field.required === 1 ? 'Required' : 'Optional'}
            </span>
            <span className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg text-xs font-medium">
              {getTypeLabel(field.type)}
            </span>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center">
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <FiCalendar className="w-3 h-3" />
              {new Date(field.created_at).toLocaleDateString()}
            </div>
            {canEditThis && (
              <div className="flex gap-1">
                {fields.length > 1 && field.sort_order > 1 && (
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => moveField(field, 'up')}
                    className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                    title="Move up"
                  >
                    <FiArrowUp className="w-4 h-4" />
                  </motion.button>
                )}
                {fields.length > 1 && field.sort_order < Math.max(...fields.map(f => f.sort_order)) && (
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => moveField(field, 'down')}
                    className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                    title="Move down"
                  >
                    <FiArrowDown className="w-4 h-4" />
                  </motion.button>
                )}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => openEditModal(field)}
                  className={`p-2 ${theme.hover} rounded-xl transition-colors ${theme.text}`}
                  title="Edit field"
                >
                  <FiEdit2 className="w-4 h-4" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => confirmDelete(field)}
                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition-colors"
                  title="Delete field"
                >
                  <FiTrash2 className="w-4 h-4" />
                </motion.button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  const FieldTableRow = ({ field }) => {
    const canEditThis = canEdit();
    
    return (
      <motion.tr
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {field.sort_order}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {field.name}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {field.label}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
          <span className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
            field.required === 1 ? theme.badge.required : theme.badge.optional
          }`}>
            {field.required === 1 ? 'Required' : 'Optional'}
          </span>
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {getTypeLabel(field.type)}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm">
          {canEditThis && (
            <div className="flex items-center gap-2">
              {fields.length > 1 && field.sort_order > 1 && (
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => moveField(field, 'up')}
                  className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Move up"
                >
                  <FiArrowUp className="w-4 h-4" />
                </motion.button>
              )}
              {fields.length > 1 && field.sort_order < Math.max(...fields.map(f => f.sort_order)) && (
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => moveField(field, 'down')}
                  className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Move down"
                >
                  <FiArrowDown className="w-4 h-4" />
                </motion.button>
              )}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => openEditModal(field)}
                className={`p-2 ${theme.hover} rounded-lg transition-colors ${theme.text}`}
                title="Edit"
              >
                <FiEdit2 className="w-4 h-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => confirmDelete(field)}
                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                title="Delete"
              >
                <FiTrash2 className="w-4 h-4" />
              </motion.button>
            </div>
          )}
        </td>
      </motion.tr>
    );
  };

  if (!canView()) {
    return (
        <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
            <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
            <FiEye className="w-10 h-10 text-red-500 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Access Denied</h3>
            <p className="text-gray-500 dark:text-gray-400">Only System Administrators can manage product fields</p>
        </div>
        </div>
    );
    }

  return (
    <div className="space-y-6 pb-20 md:pb-6">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-gradient-to-r ${theme.gradient} rounded-2xl p-6 sm:p-8 shadow-lg`}
      >
        <div className="flex items-center gap-4">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
          >
            <FiLayers className="w-7 h-7 text-white" />
          </motion.div>
          <div>
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="text-2xl md:text-3xl font-bold text-white"
            >
              Product Fields
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-indigo-100 text-sm mt-1"
            >
              Manage custom fields for products
            </motion.p>
          </div>
        </div>
      </motion.div>

      {/* Product Selection Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700"
      >
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Select Product <span className="text-red-500">*</span>
        </label>
        
        <div className="relative">
          <button
            onClick={() => setShowProductDropdown(!showProductDropdown)}
            className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-left flex items-center justify-between text-gray-900 dark:text-white hover:border-indigo-300 transition-colors"
          >
            <span className="truncate flex items-center gap-2">
              {selectedProduct ? (
                <>
                  <FiShoppingBag className={`w-4 h-4 ${theme.text}`} />
                  <span className="font-medium">{selectedProduct.name}</span>
                  <span className="text-gray-400">({selectedProduct.code})</span>
                </>
              ) : (
                <span className="text-gray-400">-- Choose a product --</span>
              )}
            </span>
            <motion.div
              animate={{ rotate: showProductDropdown ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <FiChevronDown className="w-5 h-5 text-gray-400" />
            </motion.div>
          </button>

          <AnimatePresence>
            {showProductDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute z-10 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden"
              >
                <div className="p-3 border-b border-gray-100 dark:border-gray-700">
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>

                <div className="max-h-60 overflow-y-auto scrollbar-hide">
                  {filteredProducts.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      No products found
                    </div>
                  ) : (
                    filteredProducts.map((product) => (
                      <motion.button
                        key={product.id}
                        whileHover={{ backgroundColor: 'rgba(99, 102, 241, 0.05)' }}
                        onClick={() => {
                          handleProductChange(String(product.id));
                          setShowProductDropdown(false);
                          setProductSearch('');
                        }}
                        className="w-full px-4 py-3 text-left flex items-center justify-between border-b border-gray-100 dark:border-gray-700 last:border-0"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                            <FiShoppingBag className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                              {product.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Code: {product.code}
                            </p>
                          </div>
                        </div>
                        {selectedProductId === String(product.id) && (
                          <FiCheckCircle className={`w-5 h-5 ${theme.text}`} />
                        )}
                      </motion.button>
                    ))
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {selectedProductId && (
        <>
          {/* Statistics Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {[
              { label: 'Total Fields', value: totalFields, icon: FiLayers, color: 'indigo' },
              { label: 'Required', value: requiredFields, icon: FiCheckCircle, color: 'purple' },
              { label: 'Optional', value: optionalFields, icon: FiMinusCircle, color: 'gray' },
              { label: 'Field Types', value: new Set(fields.map(f => f.type)).size, icon: FiType, color: 'blue' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-10 h-10 bg-${stat.color}-100 dark:bg-${stat.color}-900/30 rounded-lg flex items-center justify-center`}>
                    <stat.icon className={`w-5 h-5 text-${stat.color}-600 dark:text-${stat.color}-400`} />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Search and Actions Bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search fields by name or label..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-400"
                />
              </div>

              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm text-gray-900 dark:text-white"
                >
                  <option value="sort_order">Sort Order</option>
                  <option value="name">Name</option>
                  <option value="label">Label</option>
                  <option value="type">Type</option>
                </select>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-4 py-3 border rounded-xl flex items-center gap-2 transition-colors ${
                    showFilters 
                      ? 'bg-indigo-600 text-white border-indigo-600' 
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-indigo-300'
                  }`}
                >
                  <FiFilter className="w-5 h-5" />
                  <span className="hidden sm:inline">Filter</span>
                </button>

                {!isMobile && (
                  <div className="hidden sm:flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('table')}
                      className={`p-2 rounded-lg transition-colors ${
                        viewMode === 'table' 
                          ? 'bg-white dark:bg-gray-600 text-indigo-600 shadow-sm' 
                          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                      }`}
                      title="Table view"
                    >
                      <FiList className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-lg transition-colors ${
                        viewMode === 'grid' 
                          ? 'bg-white dark:bg-gray-600 text-indigo-600 shadow-sm' 
                          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                      }`}
                      title="Grid view"
                    >
                      <FiGrid className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {canCreate() && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={openCreateModal}
                    className={`px-4 py-3 bg-gradient-to-r ${theme.primary} text-white rounded-xl hover:from-indigo-700 hover:to-blue-700 transition-all shadow-lg shadow-indigo-500/20 flex items-center gap-2 whitespace-nowrap`}
                  >
                    <FiPlus className="w-5 h-5" />
                    <span className="hidden sm:inline">Add Field</span>
                  </motion.button>
                )}
              </div>
            </div>

            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700"
                >
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Required:</span>
                    <div className="flex gap-2">
                      {[
                        { value: 'all', label: 'All' },
                        { value: 'required', label: 'Required' },
                        { value: 'optional', label: 'Optional' }
                      ].map((status) => (
                        <motion.button
                          key={status.value}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setRequiredFilter(status.value)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            requiredFilter === status.value
                              ? status.value === 'required' 
                                ? 'bg-purple-600 text-white'
                                : status.value === 'optional'
                                ? 'bg-gray-600 text-white'
                                : 'bg-indigo-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          {status.label}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Fields Display */}
          {fieldsLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-indigo-200 dark:border-indigo-900 rounded-full"></div>
                <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
              </div>
            </div>
          ) : displayedFields.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center border border-gray-100 dark:border-gray-700"
            >
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-indigo-100 to-blue-100 dark:from-indigo-900/30 dark:to-blue-900/30 rounded-full flex items-center justify-center mb-4">
                <FiLayers className="w-12 h-12 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Fields Found</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                {searchTerm || requiredFilter !== 'all' || sortBy !== 'sort_order'
                  ? 'No fields match your filters.' 
                  : 'Get started by adding your first field.'}
              </p>
              {canCreate() && !searchTerm && requiredFilter === 'all' && sortBy === 'sort_order' && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={openCreateModal}
                  className={`inline-flex items-center px-4 py-2 bg-gradient-to-r ${theme.primary} text-white rounded-lg hover:from-indigo-700 hover:to-blue-700 transition-colors text-sm shadow-lg shadow-indigo-500/20`}
                >
                  <FiPlus className="mr-2 w-4 h-4" />
                  Add your first field
                </motion.button>
              )}
            </motion.div>
          ) : (
            <>
              {isMobile ? (
                <div className="grid grid-cols-1 gap-4">
                  <AnimatePresence>
                    {displayedFields.map((field) => (
                      <FieldGridCard key={field.id} field={field} />
                    ))}
                  </AnimatePresence>
                </div>
              ) : (
                <>
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <AnimatePresence>
                        {displayedFields.map((field) => (
                          <FieldGridCard key={field.id} field={field} />
                        ))}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm border border-gray-100 dark:border-gray-700">
                      <div className="overflow-x-auto scrollbar-hide">
                        <table className="w-full min-w-[800px]">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Order</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Label</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Required</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            <AnimatePresence>
                              {displayedFields.map((field) => (
                                <FieldTableRow key={field.id} field={field} />
                              ))}
                            </AnimatePresence>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowModal(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
                <div className={`bg-gradient-to-r ${theme.gradient} px-6 py-5`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 200 }}
                        className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
                      >
                        {modalType === 'create' ? (
                          <FiPlus className="w-5 h-5 text-white" />
                        ) : (
                          <FiEdit2 className="w-5 h-5 text-white" />
                        )}
                      </motion.div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {modalType === 'create' ? 'Add Field' : 'Edit Field'}
                        </h3>
                        {selectedProduct && (
                          <p className="text-sm text-indigo-100">
                            Product: {selectedProduct.name}
                          </p>
                        )}
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setShowModal(false)}
                      className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                    >
                      <FiX className="w-5 h-5 text-white" />
                    </motion.button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto scrollbar-hide p-6 bg-gray-50 dark:bg-gray-900">
                  <div className="space-y-5">
                    {/* Field Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Field Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={handleNameChange}
                        className={`
                          w-full px-4 py-3 bg-white dark:bg-gray-800 
                          border-2 rounded-xl text-sm text-gray-900 dark:text-white
                          focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                          transition-all duration-200 lowercase
                          ${formErrors.name 
                            ? 'border-red-300 focus:ring-red-500' 
                            : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300'
                          }
                        `}
                        placeholder="e.g., user_id"
                      />
                      {formErrors.name && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-xs text-red-500 flex items-center gap-1"
                        >
                          <FiAlertCircle className="w-3 h-3" />
                          {formErrors.name}
                        </motion.p>
                      )}
                      <p className="mt-2 text-xs text-gray-500">
                        Lowercase letters and underscores only
                      </p>
                    </div>

                    {/* Field Label */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Field Label <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.label}
                        onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                        className={`
                          w-full px-4 py-3 bg-white dark:bg-gray-800 
                          border-2 rounded-xl text-sm text-gray-900 dark:text-white
                          focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                          transition-all duration-200
                          ${formErrors.label 
                            ? 'border-red-300 focus:ring-red-500' 
                            : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300'
                          }
                        `}
                        placeholder="e.g., User ID"
                      />
                      {formErrors.label && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-xs text-red-500 flex items-center gap-1"
                        >
                          <FiAlertCircle className="w-3 h-3" />
                          {formErrors.label}
                        </motion.p>
                      )}
                    </div>

                    {/* Field Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Field Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        className={`
                          w-full px-4 py-3 bg-white dark:bg-gray-800 
                          border-2 rounded-xl text-sm text-gray-900 dark:text-white
                          focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                          transition-all duration-200
                          ${formErrors.type 
                            ? 'border-red-300 focus:ring-red-500' 
                            : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300'
                          }
                        `}
                      >
                        <option value="any">Any</option>
                        <option value="digit">Digits Only</option>
                        <option value="alphabet">Alphabet Only</option>
                        <option value="alphanumeric">Alphanumeric</option>
                        <option value="lower">Lowercase</option>
                        <option value="upper">Uppercase</option>
                      </select>
                      {formErrors.type && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-xs text-red-500 flex items-center gap-1"
                        >
                          <FiAlertCircle className="w-3 h-3" />
                          {formErrors.type}
                        </motion.p>
                      )}
                    </div>

                    {/* Sort Order */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Sort Order <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={formData.sort_order}
                        onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 1 })}
                        className={`
                          w-full px-4 py-3 bg-white dark:bg-gray-800 
                          border-2 rounded-xl text-sm text-gray-900 dark:text-white
                          focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                          transition-all duration-200
                          ${formErrors.sort_order 
                            ? 'border-red-300 focus:ring-red-500' 
                            : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300'
                          }
                        `}
                      />
                      {formErrors.sort_order && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-xs text-red-500 flex items-center gap-1"
                        >
                          <FiAlertCircle className="w-3 h-3" />
                          {formErrors.sort_order}
                        </motion.p>
                      )}
                    </div>

                    {/* Required Toggle */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Required
                      </label>
                      <div className="flex gap-4 p-3 bg-gray-100 dark:bg-gray-700 rounded-xl">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            checked={formData.required === true}
                            onChange={() => setFormData({ ...formData, required: true })}
                            className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Required</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            checked={formData.required === false}
                            onChange={() => setFormData({ ...formData, required: false })}
                            className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Optional</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t-2 border-gray-100 dark:border-gray-700 px-6 py-4 bg-white dark:bg-gray-800">
                  <div className="flex justify-end gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowModal(false)}
                      className="px-6 py-3 rounded-xl font-medium text-sm border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={modalType === 'create' ? handleCreate : handleUpdate}
                      disabled={isSubmitting}
                      className={`px-8 py-3 bg-gradient-to-r ${theme.primary} text-white rounded-xl font-medium text-sm hover:from-indigo-700 hover:to-blue-700 transition-all shadow-lg shadow-indigo-500/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {isSubmitting ? (
                        <>
                          <FiRefreshCw className="w-4 h-4 animate-spin" />
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <FiSave className="w-4 h-4" />
                          <span>{modalType === 'create' ? 'Create Field' : 'Update Field'}</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDeleteModal(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                      <FiAlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Field</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete this field?
                      </p>
                    </div>
                  </div>
                  
                  {fieldToDelete && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 mb-6">
                      <p className="font-medium text-gray-900 dark:text-white">{fieldToDelete.label}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Name: {fieldToDelete.name}</p>
                    </div>
                  )}
                  
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowDeleteModal(false)}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                    >
                      <FiTrash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default ProductFields;